import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  chooseZoom,
  createScanGate,
  cropRegion,
  getNativeDetector,
  pickPrimaryDetection,
  pickTorchSiblingCandidates,
  normalizeScanCandidates,
  probeTorchCapability,
  shouldAutoTorch,
  zxingFormatToName,
} from "../data/cameraScanner";
import { scanChirp, unlockScanAudio } from "../data/scanFeedback";
import { ToastifyAlert } from "../../../CommonComponents/ToastifyAlert";

// Camera barcode scanner — a live, CONTINUOUS scan surface, not a one-shot photo dialog. It stays
// open while the cashier rings a whole basket: detect → beep/flash → hand the code to the SAME scan
// pipeline the keyboard-wedge input uses (`onCode` routes into WebRegister.onScan) → keep scanning.
// Every downstream behavior — variant match, 21+ verify, stock caps, multiplier consumption,
// Quick-Add-on-miss — is therefore byte-identical to a typed scan, because it IS the typed scan.
//
// SPEED: on platforms with a real native BarcodeDetector the loop is driven by
// `requestVideoFrameCallback`, i.e. one hardware-accelerated detect per camera frame — this is as
// fast as the camera itself. The ZXing fallback (Windows/Safari/Firefox) attempts every 60ms.
//
// Z-ORDER IS DELIBERATE: this overlay sits at z 1200, BELOW every app dialog (Modal 2000, Quick Add
// 2100, age verify 2350). A scan that needs a decision — variant picker, 21+, unknown-code Quick Add
// — opens ON TOP of the camera; the cashier answers it and scanning resumes. WebRegister passes
// `paused` while any such dialog is up, and on resume the gate is re-armed (`touchAll`) so the
// still-aimed barcode does not instantly fire again.
export default function CameraScanModal({ onCode, paused, onClose }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const stopRef = useRef(false);
  const busyRef = useRef(false); // true while onCode is in flight — never double-fire across an await
  const pausedRef = useRef(!!paused);
  const gateRef = useRef(null);
  const lumaCanvasRef = useRef(null); // 32×32 sampling canvas for the auto-torch light check
  const cropCanvasRef = useRef(null); // reticle-crop canvas for the ZXing decode loop
  const diagRef = useRef({}); // torch/engine diagnostics — surfaced by triple-tapping the title
  const diagTapRef = useRef({ n: 0, t: 0 });
  const userTorchRef = useRef(false); // cashier touched the ☀ toggle ⇒ automation stands down
  const autoTorchTimerRef = useRef(null);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  const [err, setErr] = useState(null);
  const [engine, setEngine] = useState(null); // "native" | "zxing" | null (starting)
  const [facing, setFacing] = useState("environment");
  // A back camera that DOES expose the torch, found by the sibling hunt below; restarts the stream.
  const [torchDeviceId, setTorchDeviceId] = useState(null);
  const torchDeviceRef = useRef(null);
  torchDeviceRef.current = torchDeviceId;
  // Cameras the hunt has already evaluated — the loop-termination invariant: every hunt pass only
  // considers cameras NOT in this set and adds every camera it touches, so the candidate pool
  // strictly shrinks and the switch/revert dance below cannot cycle.
  const triedCamerasRef = useRef(new Set());
  // ZOOM — the small-barcode (1"×1" shelf tag) answer: fill the reticle from a distance the lens can
  // still FOCUS at. Hardware zoom when the camera exposes it; else digital (scaled preview + tighter
  // decode crop — which also skips the downscale, so the decoder gets MORE px per bar, not fewer).
  const ZOOMS = [1, 1.5, 2.5];
  const [zoomIdx, setZoomIdx] = useState(0);
  const zoomIdxRef = useRef(0);
  zoomIdxRef.current = zoomIdx;
  const [videoScale, setVideoScale] = useState(1); // CSS preview scale in digital mode
  const hwZoomCapsRef = useRef(null); // caps.zoom of the current track, or null
  const digitalZoomRef = useRef(1); // tightens the ZXing crop when hardware zoom is absent
  const [torchAvail, setTorchAvail] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [canFlip, setCanFlip] = useState(false);
  const [last, setLast] = useState(null); // { code, ok }
  const [flash, setFlash] = useState(null); // "ok" | "bad"
  const [showDiag, setShowDiag] = useState(false); // triple-tap the title to toggle
  const [, setDiagTick] = useState(0); // re-render pulse so the readout tracks a late-finishing probe

  useEffect(() => {
    if (!showDiag) return undefined;
    const id = setInterval(() => setDiagTick((t) => t + 1), 800);
    return () => clearInterval(id);
  }, [showDiag]);

  if (!gateRef.current) gateRef.current = createScanGate();

  // Pause without tearing the stream down — a stopped camera takes ~1s to restart, and the cashier
  // is mid-sale. On RESUME, re-arm the gate: the sliding suppress window expired while the blocking
  // dialog was up, and the barcode is very likely still under the reticle.
  useEffect(() => {
    const was = pausedRef.current;
    pausedRef.current = !!paused;
    if (was && !paused) gateRef.current.touchAll();
  }, [paused]);

  // Mean luma of the current video frame, via a 32×32 downsample — cheap enough to run casually.
  const sampleLuma = useCallback(() => {
    try {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return null;
      if (!lumaCanvasRef.current) {
        lumaCanvasRef.current = document.createElement("canvas");
        lumaCanvasRef.current.width = 32;
        lumaCanvasRef.current.height = 32;
      }
      const g = lumaCanvasRef.current.getContext("2d", { willReadFrequently: true });
      g.drawImage(video, 0, 0, 32, 32);
      const d = g.getImageData(0, 0, 32, 32).data;
      let sum = 0;
      for (let i = 0; i < d.length; i += 4) sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      return sum / (d.length / 4);
    } catch {
      return null; // tainted/odd frame — the light check just skips this round
    }
  }, []);

  // One torch path for both the auto light-check and the torch button, so state can never fork.
  // Tries BOTH constraint shapes — `advanced: [{torch}]` (the spec's) and bare `{torch}` (the only
  // shape some Android builds honor) — and verifies via `getSettings().torch`, because `advanced`
  // sets may be DISCARDED SILENTLY by spec and a resolved applyConstraints proves nothing.
  // ⚠️ FAILURE NEVER HIDES THE BUTTON. An earlier version did, and in a dim room the auto-torch
  // would fire seconds after the button appeared, fail, and remove it before the cashier ever saw
  // it — indistinguishable from "no torch at all" (a likely contributor to three rounds of "still
  // not visible"). A visible control that says it failed is debuggable; a vanished one is not.
  const applyTorch = useCallback(async (on, { manual = false } = {}) => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const track = streamRef.current && streamRef.current.getVideoTracks()[0];
      if (!track) return false;
      for (const constraints of [{ advanced: [{ torch: on }] }, { torch: on }]) {
        try {
          await track.applyConstraints(constraints);
          const settings = track.getSettings ? track.getSettings() || {} : {};
          // Only trust the outcome when the track can report it; a track without getSettings
          // support gets the benefit of the doubt (the constraint resolved).
          if (!track.getSettings || !!settings.torch === !!on) {
            setTorchOn(on);
            return true;
          }
        } catch (e) {
          diagRef.current.applyTorchErr = (e && e.name) || "err";
          /* try the other shape / the retry round */
        }
      }
      await new Promise((r) => setTimeout(r, 350));
    }
    if (manual) ToastifyAlert("The torch didn't respond on this camera", "error");
    return false;
  }, []);

  // One zoom path for the auto-default and the cycle button. Hardware zoom clamps the factor into
  // the camera's range and keeps the preview 1:1; digital mode scales the PREVIEW with CSS and
  // tightens the decode crop by the same factor, so what the cashier sees is what the decoder gets.
  // (Rare mismatch: a native-engine platform without hardware zoom — macOS webcams — zooms the
  // preview but the native detector still reads the full frame. Cosmetic only; decode is unhurt.)
  const applyZoom = useCallback(async (idx) => {
    setZoomIdx(idx);
    const factor = ZOOMS[idx];
    const track = streamRef.current && streamRef.current.getVideoTracks()[0];
    const zc = hwZoomCapsRef.current;
    if (track && zc && typeof zc.max === "number" && zc.max >= 1.3) {
      const min = typeof zc.min === "number" ? zc.min : 1;
      const z = Math.min(Math.max(factor, min), zc.max);
      try {
        await track.applyConstraints({ advanced: [{ zoom: z }] });
        digitalZoomRef.current = 1;
        setVideoScale(1);
        diagRef.current.zoom = `hw ${z}`;
        return;
      } catch {
        /* hardware said no — fall through to digital */
      }
    }
    digitalZoomRef.current = factor;
    setVideoScale(factor);
    diagRef.current.zoom = factor === 1 ? "off" : `digital ${factor}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDetections = useCallback(
    async (detections, video) => {
      const det = pickPrimaryDetection(detections, video && video.videoWidth, video && video.videoHeight);
      const text = det && String(det.text || "").trim();
      if (!text) return;
      if (!gateRef.current.accept(text)) return;
      busyRef.current = true;
      try {
        // Haptics: Android only — iOS Safari has no vibration API at all, so there the chirp and the
        // full-screen flash ARE the feedback. Guarded, not polyfilled.
        if (navigator.vibrate) navigator.vibrate(35);
        const res = await onCodeRef.current(normalizeScanCandidates(text, det.format));
        const ok = !!(res && res.ok);
        setLast({ code: text, ok });
        setFlash(ok ? "ok" : "bad");
        scanChirp(ok);
        setTimeout(() => setFlash(null), 350);
      } finally {
        busyRef.current = false;
      }
    },
    []
  );

  // Camera + engine lifecycle. Re-runs on a camera flip; full teardown on unmount.
  useEffect(() => {
    let cancelled = false;
    stopRef.current = false;
    setErr(null);
    setEngine(null);
    setTorchAvail(false);
    setTorchOn(false);

    (async () => {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErr("Camera scanning needs a secure (HTTPS) connection and a device with a camera.");
        return;
      }
      let stream;
      try {
        // 1080p, up from 720p (round 4): pixels-per-module is the biggest lever against iPhone
        // focus-hunt blur, and the crop cap was raised in step so they reach the decoder. When the
        // sibling hunt has identified the torch-capable back camera, bind it by deviceId.
        const size = { width: { ideal: 1920 }, height: { ideal: 1080 } };
        const byFacing = { facingMode: { ideal: facing }, ...size };
        if (torchDeviceId && facing === "environment") {
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              audio: false,
              video: { deviceId: { exact: torchDeviceId }, ...size },
            });
          } catch {
            // The remembered device vanished — fall back to facingMode rather than erroring out.
            stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: byFacing });
          }
        } else {
          stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: byFacing });
        }
      } catch (e) {
        const denied = e && (e.name === "NotAllowedError" || e.name === "SecurityError");
        setErr(
          denied
            ? "Camera permission was denied. Allow camera access for this site (the padlock icon in the address bar), then try again."
            : `Couldn't start the camera${e && e.name ? ` (${e.name})` : ""}. Check that another app isn't using it.`
        );
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play().catch(() => {});

      // Browser identity for the diag readout — Chrome major + Android/iOS version is usually the
      // difference between "device quirk" and "old browser".
      try {
        diagRef.current.ua = (navigator.userAgent.match(/Android [^;)]+|Chrome\/\d+|CriOS\/\d+|SamsungBrowser\/[\d.]+|iPhone OS [\d_]+/g) || ["?"]).join(" ");
      } catch {
        /* informational only */
      }

      // Continuous autofocus + a modest default zoom (both best-effort, immediate). The zoom is the
      // focus fix that actually works cross-platform: ~1.5× lets the barcode fill the frame from
      // FURTHER away — inside the lens's comfortable focus range — attacking the close-range blur
      // behind most "camera won't read it" moments (see chooseZoom).
      try {
        const track = stream.getVideoTracks()[0];
        diagRef.current.label = String(track.label || "").slice(0, 60);
        diagRef.current.res = `${video.videoWidth}x${video.videoHeight}`;
        const caps = track.getCapabilities ? track.getCapabilities() : {};
        if (Array.isArray(caps.focusMode) && caps.focusMode.includes("continuous")) {
          track.applyConstraints({ advanced: [{ focusMode: "continuous" }] }).catch(() => {});
        }
        if (facing === "environment") {
          hwZoomCapsRef.current = caps.zoom || null;
          // Default: 1.5× where HARDWARE zoom exists (phones — helps focus at counter distance);
          // 1× where it doesn't (desktop webcams — a surprise digital crop would shrink their band).
          const startIdx =
            zoomIdxRef.current !== 0 ? zoomIdxRef.current : chooseZoom(caps.zoom) ? 1 : 0;
          applyZoom(startIdx);
        } else {
          hwZoomCapsRef.current = null;
          digitalZoomRef.current = 1;
          setVideoScale(1);
        }
      } catch {
        /* capability probing is optional */
      }

      // TORCH — probed with RETRIES, in the background. Android Chrome often doesn't report `torch`
      // in getCapabilities() until the stream settles: a single read at t=0 said "no torch" on a
      // phone that has one, hiding the button and disarming auto-torch (real-device report,
      // 2026-08-17). The button appears — and auto-torch arms — whenever the probe resolves. Every
      // step writes into diagRef so the triple-tap readout can show WHY a device ended up torchless.
      probeTorchCapability(stream.getVideoTracks()[0], { diag: diagRef.current }).then((capable) => {
        diagRef.current.torch = capable ? "available" : "unavailable";
        // SIBLING HUNT (round 4, reworked round 6): multi-lens Androids can bind `facingMode:
        // environment` to a back camera WITHOUT the flash — the confirmed live case ("camera 2,
        // facing back": rich caps, no torch, empty fillLight) while the primary camera owns the
        // flash. If the bound camera has no torch, evaluate the OTHER back cameras:
        //  · concurrency-capable devices: open the sibling at thumbnail size beside the main stream
        //    and ask it for torch directly;
        //  · EXCLUSIVE-CAMERA devices (the confirmed case — the side-probe dies with
        //    NotReadableError because the hardware is single-tenant): the sibling cannot be examined
        //    from the side at all, so SWITCH the main stream onto it and let the normal torch probe
        //    run there. If it also has no torch, the pool empties and we revert to the platform's
        //    default camera. `triedCamerasRef` makes every pass strictly shrink the pool — no cycles.
        if (!cancelled && !capable && facing === "environment") {
          (async () => {
            try {
              const track = stream.getVideoTracks()[0];
              const currentId = ((track.getSettings && track.getSettings()) || {}).deviceId || "";
              triedCamerasRef.current.add(currentId);
              const devices = await navigator.mediaDevices.enumerateDevices();
              const candidates = pickTorchSiblingCandidates(devices, currentId).filter(
                (c) => !triedCamerasRef.current.has(c.deviceId)
              );
              diagRef.current.siblings = candidates.length;
              for (const c of candidates) {
                if (cancelled) return;
                let probeStream = null;
                try {
                  probeStream = await navigator.mediaDevices.getUserMedia({
                    audio: false,
                    video: { deviceId: { exact: c.deviceId }, width: { ideal: 320 }, height: { ideal: 240 } },
                  });
                  const t = probeStream.getVideoTracks()[0];
                  let torch = !!((t.getCapabilities && t.getCapabilities()) || {}).torch;
                  if (!torch) {
                    await new Promise((r) => setTimeout(r, 300)); // capabilities can populate late here too
                    torch = !!((t.getCapabilities && t.getCapabilities()) || {}).torch;
                  }
                  triedCamerasRef.current.add(c.deviceId);
                  if (torch) {
                    diagRef.current.siblingTorch = String(c.label || c.deviceId).slice(0, 40);
                    if (!cancelled) setTorchDeviceId(c.deviceId); // effect re-runs on that camera
                    return;
                  }
                } catch (e) {
                  const name = (e && e.name) || "err";
                  diagRef.current.siblingErr = name;
                  if (name === "NotReadableError" || name === "AbortError" || name === "TrackStartError") {
                    // Exclusive hardware — switch the main stream to this camera and probe there.
                    triedCamerasRef.current.add(c.deviceId);
                    diagRef.current.switchedTo = String(c.label || c.deviceId).slice(0, 40);
                    if (!cancelled) setTorchDeviceId(c.deviceId);
                    return;
                  }
                } finally {
                  if (probeStream) probeStream.getTracks().forEach((x) => x.stop());
                }
              }
              // Pool exhausted. If we are sitting on a speculative switch that turned out torchless,
              // go back to the platform's default pick — it was chosen for a reason.
              if (!cancelled && candidates.length === 0 && torchDeviceRef.current) {
                diagRef.current.reverted = true;
                setTorchDeviceId(null);
              }
            } catch {
              /* enumeration failed — nothing further to try */
            }
          })();
        }
        if (cancelled || !capable) return;
        setTorchAvail(true);
        // AUTO-TORCH: in bad light the torch comes ON by itself — a cashier mid-sale should not
        // have to discover a button to make scanning work. Needs a CONSISTENTLY dark scene
        // (shouldAutoTorch; auto-exposure has long settled by the time the probe resolves), and
        // keeps re-checking slowly so walking into a dark corner mid-basket still lights up. It
        // only ever turns the torch ON, never off, and it stands down entirely once the cashier
        // touches the torch button — a human choice outranks the heuristic in both directions.
        const settle = setTimeout(async () => {
          const samples = [];
          for (let i = 0; i < 3; i++) {
            const l = sampleLuma();
            if (l != null) samples.push(l);
            await new Promise((r) => setTimeout(r, 250));
          }
          if (!cancelled && !userTorchRef.current && shouldAutoTorch(samples)) applyTorch(true);
          if (cancelled) return;
          autoTorchTimerRef.current = {
            ...(autoTorchTimerRef.current || {}),
            interval: setInterval(async () => {
              if (cancelled || userTorchRef.current) return;
              const a = sampleLuma();
              await new Promise((r) => setTimeout(r, 250));
              const b = sampleLuma();
              if (!cancelled && !userTorchRef.current && shouldAutoTorch([a, b])) applyTorch(true);
            }, 4000),
          };
        }, 300);
        autoTorchTimerRef.current = { timeout: settle };
      });

      try {
        const cams = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === "videoinput");
        setCanFlip(cams.length > 1);
      } catch {
        /* device list is optional */
      }

      const detector = await getNativeDetector();
      if (cancelled) return;
      if (detector) {
        setEngine("native");
        // One detect per camera frame (rVFC), sequential so a slow frame skips rather than stacks.
        const step = async () => {
          if (stopRef.current) return;
          if (!pausedRef.current && !busyRef.current && video.readyState >= 2) {
            try {
              const found = await detector.detect(video);
              if (found && found.length) {
                await handleDetections(
                  found.map((d) => ({ text: d.rawValue, format: d.format, box: d.boundingBox })),
                  video
                );
              }
            } catch {
              /* a bad frame is not an error state — the next frame retries */
            }
          }
          if (stopRef.current) return;
          if (video.requestVideoFrameCallback) video.requestVideoFrameCallback(() => step());
          else setTimeout(step, 60);
        };
        step();
      } else {
        // ZXing fallback, loaded only here so no other platform ever ships it in the hot path.
        //
        // OWN DECODE LOOP, NOT decodeFromStream. The stock loop decodes the FULL frame, and on a
        // real iPhone that read 1 barcode in 4 (device report, 2026-08-17): the binarizer spends its
        // budget on shelves and walls. Instead each attempt decodes the RETICLE CROP — the center
        // band the UI tells the cashier to use — which makes the barcode dominate the image, and the
        // saved time pays for TRY_HARDER (more scan lines, more blur/tilt tolerance).
        try {
          const [{ BrowserMultiFormatReader }, lib] = await Promise.all([
            import("@zxing/browser"),
            import("@zxing/library"),
          ]);
          if (cancelled) return;
          const { BarcodeFormat, DecodeHintType } = lib;
          const hints = new Map();
          // Retail-relevant formats only — every extra decoder runs on EVERY attempt on a phone CPU.
          // (Codabar/Code93/DataMatrix are deliberately absent from the fallback; the native engine
          // keeps them for free.)
          hints.set(DecodeHintType.POSSIBLE_FORMATS, [
            BarcodeFormat.UPC_A,
            BarcodeFormat.UPC_E,
            BarcodeFormat.EAN_13,
            BarcodeFormat.EAN_8,
            BarcodeFormat.CODE_128,
            BarcodeFormat.CODE_39,
            BarcodeFormat.ITF,
            BarcodeFormat.QR_CODE,
          ]);
          hints.set(DecodeHintType.TRY_HARDER, true);
          const reader = new BrowserMultiFormatReader(hints);
          if (!cropCanvasRef.current) cropCanvasRef.current = document.createElement("canvas");
          const crop = cropCanvasRef.current;
          const g = crop.getContext("2d", { willReadFrequently: true });
          setEngine("zxing");
          // Decode stats feed BOTH the triple-tap diag readout (numbers instead of "not so well")
          // and the ADAPTIVE decode size below: a slow phone shrinks the crop until an attempt costs
          // well under 150ms, a fast one grows it back toward full detail. A constant attempt RATE
          // is what kills the "sometimes fast, sometimes slow" feel — the variance was attempts
          // queuing behind slow decodes on weaker hardware.
          const stats = { attempts: 0, emaMs: 0, hits: 0 };
          let capW = 1280;
          let lastVT = -1;
          const step = async () => {
            if (stopRef.current) return;
            let delay = 70;
            if (!pausedRef.current && !busyRef.current && video.readyState >= 2 && video.videoWidth) {
              if (video.currentTime === lastVT) {
                delay = 24; // the frame hasn't advanced — don't burn a decode on identical pixels
              } else {
                lastVT = video.currentTime;
                const t0 = performance.now();
                try {
                    // Digital zoom = a proportionally tighter crop; hardware zoom leaves this at 0.86.
                  const r = cropRegion(video.videoWidth, video.videoHeight, {
                    maxWidth: capW,
                    widthFrac: 0.86 / digitalZoomRef.current,
                  });
                  if (crop.width !== r.dw) crop.width = r.dw;
                  if (crop.height !== r.dh) crop.height = r.dh;
                  g.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, r.dw, r.dh);
                  const result = reader.decodeFromCanvas(crop);
                  if (result) {
                    stats.hits++;
                    await handleDetections(
                      [{ text: result.getText(), format: zxingFormatToName(result.getBarcodeFormat(), BarcodeFormat), box: null }],
                      video
                    );
                  }
                } catch {
                  /* NotFoundException — the normal miss; the next frame retries */
                }
                stats.attempts++;
                const dt = performance.now() - t0;
                stats.emaMs = stats.emaMs ? stats.emaMs * 0.9 + dt * 0.1 : dt;
                if (stats.attempts % 10 === 0) {
                  if (stats.emaMs > 140 && capW > 640) capW = Math.max(640, capW - 160);
                  else if (stats.emaMs < 60 && capW < 1280) capW = Math.min(1280, capW + 160);
                  diagRef.current.decode = `${stats.attempts} tries, ~${Math.round(stats.emaMs)}ms each, ${stats.hits} hits, cap ${capW}`;
                }
              }
            }
            if (stopRef.current) return;
            // Plain timer, NOT requestVideoFrameCallback: rVFC starves whenever the window loses
            // focus/visibility (observed live — zero decode attempts), and ~14 attempts/sec is
            // already faster than a hand can present barcodes.
            setTimeout(step, delay);
          };
          step();
        } catch {
          if (!cancelled) setErr("The barcode engine failed to load. Check the connection and reopen the scanner.");
        }
      }
    })();

    return () => {
      cancelled = true;
      stopRef.current = true;
      if (autoTorchTimerRef.current) {
        clearTimeout(autoTorchTimerRef.current.timeout);
        clearInterval(autoTorchTimerRef.current.interval);
        autoTorchTimerRef.current = null;
      }
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [facing, torchDeviceId, handleDetections, sampleLuma, applyTorch, applyZoom]);

  // Escape closes, like every other register dialog.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !pausedRef.current) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Best-effort audio re-unlock on mount. The REAL unlock happens in the ⌖ click handler
  // (WebRegister calls unlockScanAudio inside the tap) — on a real iPhone, mount-time unlock alone
  // shipped as "scanned, but silent" because iOS keys on a source STARTING during the gesture. The
  // shared context is deliberately never closed: one unlock covers the whole session.
  useEffect(() => {
    unlockScanAudio();
  }, []);

  const toggleTorch = async () => {
    userTorchRef.current = true; // manual choice — the auto-torch heuristic stands down
    await applyTorch(!torchOn, { manual: true });
  };

  return (
    <div className="qv-cam" role="dialog" aria-modal="true" aria-label="Camera barcode scanner">
      {/* playsInline is REQUIRED for iOS Safari — without it the video hijacks into fullscreen.
          The transform is the DIGITAL zoom preview (hardware zoom keeps it at 1). */}
      <video
        ref={videoRef}
        className="qv-cam-video"
        playsInline
        muted
        autoPlay
        style={videoScale !== 1 ? { transform: `scale(${videoScale})` } : undefined}
      />

      {flash && <div className={`qv-cam-flash qv-cam-flash--${flash}`} aria-hidden="true" />}

      {/* Top bar carries ONLY the controls (user decision, 2026-08-17: the "Camera scan" title and
          engine tag are gone — a cashier needs torch, zoom, close, nothing to read). Triple-tapping
          the bar's EMPTY area still toggles the diagnostics readout — the remote-debug channel that
          solved the Android torch mystery — guarded to the bar itself so rapid taps on the torch or
          zoom buttons never count. */}
      <div
        className="qv-cam-top"
        onClick={(e) => {
          if (e.target !== e.currentTarget) return; // taps on buttons don't count
          const now = Date.now();
          const s = diagTapRef.current;
          s.n = now - s.t < 1200 ? s.n + 1 : 1;
          s.t = now;
          if (s.n >= 3) {
            s.n = 0;
            setShowDiag((v) => !v);
          }
        }}
      >
        <div className="qv-cam-top-actions">
          {/* Zoom cycle — THE control for small (1"×1") shelf-tag barcodes: zoom in, hold the tag a
              hand-width away where the lens can focus, let it fill the frame. */}
          {facing === "environment" && !err && (
            <button
              type="button"
              className={ZOOMS[zoomIdx] !== 1 ? "qv-btn qv-btn--icon is-on qv-cam-zoombtn" : "qv-btn qv-btn--icon qv-cam-zoombtn"}
              onClick={() => applyZoom((zoomIdx + 1) % ZOOMS.length)}
              title="Zoom (for small barcodes)"
              aria-label={`Zoom ${ZOOMS[zoomIdx]}x — tap to change`}
            >
              <span className="qv-num">{ZOOMS[zoomIdx]}×</span>
            </button>
          )}
          {torchAvail && (
            <button
              type="button"
              className={torchOn ? "qv-btn qv-btn--icon is-on" : "qv-btn qv-btn--icon"}
              onClick={toggleTorch}
              aria-pressed={torchOn}
              title="Torch"
              aria-label="Torch"
            >
              {/* Flash bolt (Material flash_on) — ☀ read as "brightness", not "flashlight". */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M7 2v11h3v9l7-12h-4l4-8H7z" />
              </svg>
            </button>
          )}
          {canFlip && (
            <button
              type="button"
              className="qv-btn qv-btn--icon"
              onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
              title="Switch camera"
              aria-label="Switch camera"
            >
              {/* Camera with rotate arrows INSIDE the body (flip_camera_ios style) — bare circular
                  arrows (⟳) read as "reload", per real-device feedback. */}
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4 7h3.2L9.4 4.5h5.2L16.8 7H20a1.5 1.5 0 0 1 1.5 1.5v9A1.5 1.5 0 0 1 20 19H4a1.5 1.5 0 0 1-1.5-1.5v-9A1.5 1.5 0 0 1 4 7Z" />
                <path d="M9.1 11.4a3.1 3.1 0 0 1 5.8 0" />
                <path d="M15.4 9.4v2h-2" />
                <path d="M14.9 14.3a3.1 3.1 0 0 1-5.8 0" />
                <path d="M8.6 16.3v-2h2" />
              </svg>
            </button>
          )}
          <button type="button" className="qv-btn qv-btn--icon" onClick={onClose} aria-label="Close camera scanner">
            <span aria-hidden="true">✕</span>
          </button>
        </div>
      </div>

      {showDiag && (
        <pre
          className="qv-cam-diag"
          onClick={() => {
            // Paste beats screenshot: tapping the panel copies the readout as text.
            try {
              const text = JSON.stringify(
                { engine: engine || "starting", torchBtn: torchAvail ? "shown" : "hidden", ...diagRef.current },
                null,
                1
              );
              if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(
                  () => ToastifyAlert("Diagnostics copied — paste them in the chat", "success"),
                  () => {}
                );
              }
            } catch {
              /* copy is a nicety */
            }
          }}
        >
          {"tap panel to copy\n" +
            JSON.stringify(
              {
                engine: engine || "starting",
                torchBtn: torchAvail ? "shown" : "hidden",
                ...diagRef.current,
              },
              null,
              1
            )}
        </pre>
      )}

      {err ? (
        <div className="qv-cam-err">
          <div className="qv-cam-err-title">Camera unavailable</div>
          <div className="qv-cam-err-text">{err}</div>
          <button type="button" className="qv-btn qv-btn--primary" onClick={onClose}>
            Back to register
          </button>
        </div>
      ) : (
        <>
          <div className="qv-cam-stage" aria-hidden="true">
            <div className={flash === "ok" ? "qv-cam-frame is-hit" : "qv-cam-frame"}>
              {!paused && engine && <div className="qv-cam-laser" />}
            </div>
          </div>
          <div className="qv-cam-foot">
            {paused ? (
              <span className="qv-cam-hint">Scanning paused while a dialog is open…</span>
            ) : !engine ? (
              <span className="qv-cam-hint">Starting camera…</span>
            ) : last ? (
              <span className={last.ok ? "qv-cam-last qv-cam-last--ok" : "qv-cam-last qv-cam-last--bad"}>
                <span className="qv-num">{last.code}</span>
                {last.ok ? " · added" : " · not in the catalog"}
              </span>
            ) : (
              <span className="qv-cam-hint">Hold a barcode inside the frame — it scans automatically</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
